import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { PageErrorBoundary } from './components/PageErrorBoundary';
import { useState } from 'react';
import type { ReactNode } from 'react';
import { AppShell } from './components/AppShell';
import { ToastViewport } from './components/ToastViewport';
import { InstitutionalFooter } from './components/InstitutionalFooter';
import { Button, Dialog, Field, InlineMessage } from './components/ui';
import { useApp } from './domain/AppContext';
import type { UserRole } from './domain/types';
import { CalendarPage } from './pages/CalendarPage';
import { DashboardPage } from './pages/DashboardPage';
import { EnrollmentWizardPage } from './pages/EnrollmentWizardPage';
import { LoginPage } from './pages/LoginPage';
import { HighlightsWallPage } from './pages/HighlightsWallPage';
import { OlympiadDetailPage } from './pages/OlympiadDetailPage';
import { OlympiadsPage } from './pages/OlympiadsPage';
import { ProfilePage } from './pages/ProfilePage';
import { PrivacyPage } from './pages/PrivacyPage';
import { ActivitiesPage } from './pages/admin/ActivitiesPage';
import { CommunicationsPage } from './pages/admin/CommunicationsPage';
import { CredentialsPage } from './pages/admin/CredentialsPage';
import { EnrollmentsPage } from './pages/admin/EnrollmentsPage';
import { InstitutionPage, SettingsPage } from './pages/admin/InstitutionPage';
import { NewOlympiadPage } from './pages/admin/NewOlympiadPage';
import { ReportsPage } from './pages/admin/ReportsPage';
import { ResultsPage } from './pages/admin/ResultsPage';
import { StudentDetailPage, StudentsPage } from './pages/admin/StudentsPage';
import { TeachersPage } from './pages/admin/TeachersPage';

export function App() {
  const { profile, authLoading, authProgress, data, dataError, dataLoading, refresh, bootstrapError, retryBootstrap } = useApp();
  const location = useLocation();
  if (bootstrapError) return <main id="conteudo-principal" className="app-bootstrap bootstrap-error"><img src="./brand/icon-05-sangria-2048.png" alt="" /><h1>O carregamento não foi concluído</h1><p role="alert">{bootstrapError}</p><Button onClick={retryBootstrap}>Tentar novamente</Button></main>;
  if (authLoading) return <div className="app-bootstrap" role="status" aria-live="polite"><img src="./brand/icon-05-sangria-2048.png" alt="" /><div className="bootstrap-progress" role="progressbar" aria-label="Carregando a Oficina Sapiens" aria-valuemin={0} aria-valuemax={100} aria-valuenow={authProgress}><span style={{ width: `${authProgress}%` }} /></div><strong>{authProgress}%</strong><p>Preparando a Oficina Sapiens...</p></div>;
  // The backend rejects application rows while this flag is true. Keeping a
  // single frontend route here also prevents deep-link navigation around it.
  if (profile?.mustChangePassword) return <><MandatoryPasswordChange /><ToastViewport /></>;
  if (profile && !data && dataError) return <div className="app-bootstrap"><InlineMessage type="error" title="Não foi possível carregar os dados da unidade">{dataError}</InlineMessage><Button loading={dataLoading} onClick={() => void refresh()}>Tentar novamente</Button><ToastViewport /></div>;
  return (
    <>
      <PageErrorBoundary key={location.pathname}><Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/privacidade" element={<PrivacyPage />} />
        <Route element={<AuthenticatedRoute><AppShell /></AuthenticatedRoute>}>
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/mural-de-destaques" element={<HighlightsWallPage />} />
          <Route path="/olimpiadas" element={<OlympiadsPage />} />
          <Route path="/olimpiadas/nova" element={<PermissionRoute permission="olympiads:manage"><NewOlympiadPage /></PermissionRoute>} />
          <Route path="/olimpiadas/:slug/editar" element={<PermissionRoute permission="olympiads:manage"><NewOlympiadPage /></PermissionRoute>} />
          <Route path="/olimpiadas/:slug" element={<OlympiadDetailPage />} />
          <Route path="/olimpiadas/:slug/inscricao" element={<RoleRoute roles={['student']}><EnrollmentWizardPage /></RoleRoute>} />
          <Route path="/inscricoes" element={<EnrollmentAccessRoute><EnrollmentsPage /></EnrollmentAccessRoute>} />
          <Route path="/estudantes" element={<PermissionRoute permission="enrollments:view"><StudentsPage /></PermissionRoute>} />
          <Route path="/estudantes/:id" element={<PermissionRoute permission="enrollments:view"><StudentDetailPage /></PermissionRoute>} />
          <Route path="/professores" element={<RoleRoute roles={['admin']}><TeachersPage /></RoleRoute>} />
          <Route path="/calendario" element={<CalendarPage />} />
          <Route path="/resultados" element={<ResultsPage />} />
          <Route path="/relatorios" element={<PermissionRoute permission="reports:view"><ReportsPage /></PermissionRoute>} />
          <Route path="/credenciais" element={<PermissionRoute permission="credentials:view"><CredentialsPage /></PermissionRoute>} />
          <Route path="/comunicados" element={<RoleRoute roles={['admin']}><CommunicationsPage /></RoleRoute>} />
          <Route path="/atividades" element={<RoleRoute roles={['admin']}><ActivitiesPage /></RoleRoute>} />
          <Route path="/instituicao" element={<RoleRoute roles={['teacher', 'admin']}><InstitutionPage /></RoleRoute>} />
          <Route path="/configuracoes" element={<SettingsAccessRoute />} />
          <Route path="/perfil" element={<ProfilePage />} />
        </Route>
        <Route path="*" element={<Navigate to={profile ? '/dashboard' : '/login'} replace />} />
      </Routes></PageErrorBoundary>
      <InstitutionalFooter />
      <ToastViewport />
    </>
  );
}

function MandatoryPasswordChange() {
  const { changePassword, notify } = useApp();
  const [newPassword, setNewPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    if (newPassword !== confirmation) return notify({ type: 'error', title: 'As novas senhas não coincidem' });
    if (newPassword === 'inicial@123') return notify({ type: 'error', title: 'Escolha uma senha pessoal diferente da senha inicial' });
    setLoading(true); const result = await changePassword('', newPassword); setLoading(false);
    notify(result.ok ? { type: 'success', title: 'Nova senha criada', description: 'Os demais recursos da plataforma foram liberados.' } : { type: 'error', title: 'Senha não alterada', description: result.error.message });
  };
  return <Dialog open title="Crie uma nova senha" description="Sua sessão já foi validada com a senha inicial. Agora defina a senha pessoal para continuar." onClose={() => undefined} closeable={false} actions={<Button loading={loading} onClick={() => void submit()}>Criar nova senha</Button>}><div className="dialog-form"><InlineMessage title="Proteja sua conta">Use uma senha diferente de inicial@123 e não a compartilhe por mensagem.</InlineMessage><Field label="Nova senha" hint="Ao menos 10 caracteres, com maiúscula, minúscula e número."><input type="password" autoComplete="new-password" autoFocus value={newPassword} onChange={(event) => setNewPassword(event.target.value)} /></Field><Field label="Confirmar nova senha"><input type="password" autoComplete="new-password" value={confirmation} onChange={(event) => setConfirmation(event.target.value)} /></Field></div></Dialog>;
}

function AuthenticatedRoute({ children }: { children: ReactNode }) {
  const { profile } = useApp();
  const location = useLocation();
  return profile ? children : <Navigate to="/login" replace state={{ from: location.pathname }} />;
}

function RoleRoute({ roles, children }: { roles: UserRole[]; children: ReactNode }) {
  const { profile } = useApp();
  return profile && roles.includes(profile.role) ? children : <Navigate to="/dashboard" replace />;
}

function PermissionRoute({ permission, children }: { permission: string; children: ReactNode }) {
  const { profile } = useApp();
  const legacyOlympiadManage = permission === 'olympiads:manage' && Boolean(profile?.permissions.includes('olympiads:create') || profile?.permissions.includes('olympiads:publish'));
  return profile && (profile.permissions.includes('*') || profile.permissions.includes(permission) || legacyOlympiadManage) ? children : <Navigate to="/dashboard" replace />;
}

function EnrollmentAccessRoute({ children }: { children: ReactNode }) {
  const { profile } = useApp();
  const allowed = profile?.role === 'student' || Boolean(profile && (profile.permissions.includes('*') || profile.permissions.includes('enrollments:view')));
  return allowed ? children : <Navigate to="/dashboard" replace />;
}

function SettingsAccessRoute() {
  const { profile } = useApp();
  if (!profile) return <Navigate to="/login" replace />;
  return profile.isSystemManager ? <SettingsPage /> : <ProfilePage />;
}
